import {
    ErrorMergeOptions,
    ErrorMessage,
    ErrorProcessingResult,
    ErrorStateMap,
    ErrorType,
} from '../types';

import { DEFAULT_ERROR_MESSAGE_SEPARATOR, ERROR_TYPES } from './constants';

/**
 * Normalizes error messages to a consistent array format
 * @param errorMessage - The error message(s) to normalize
 * @param separator - Custom separator for string parsing
 * @returns Normalized array of error messages
 */
export function normalizeErrorMessage(
    errorMessage: ErrorMessage,
    separator: string = DEFAULT_ERROR_MESSAGE_SEPARATOR
): string[] {
    if (!errorMessage) {
        return [];
    }

    if (Array.isArray(errorMessage)) {
        return errorMessage.filter(Boolean);
    }

    if (typeof errorMessage === 'string') {
        return errorMessage
            .split(separator)
            .map((error) => {
                return error.trim();
            })
            .filter(Boolean);
    }

    return [];
}

/**
 * Merges multiple error arrays with deduplication and filtering
 * @param errorArrays - Arrays of errors to merge
 * @param options - Merge options
 * @returns Merged and processed error array
 */
export function mergeErrorArrays(
    errorArrays: string[][],
    options: ErrorMergeOptions = {}
): string[] {
    const { removeDuplicates = true, filterEmpty = true } = options;

    const merged = errorArrays.flat();

    if (filterEmpty) {
        const filtered = merged.filter(Boolean);
        return removeDuplicates ? Array.from(new Set(filtered)) : filtered;
    }

    return removeDuplicates ? Array.from(new Set(merged)) : merged;
}

/**
 * Converts error array to a single string with separator
 * @param errors - Array of error messages
 * @param separator - Separator string
 * @returns Joined error string or empty string
 */
export function errorsToString(
    errors: string[],
    separator: string = DEFAULT_ERROR_MESSAGE_SEPARATOR
): string {
    return errors.filter(Boolean).join(separator);
}

/**
 * Extracts errors from error state map by type
 * @param errorStateMap - Map of errors organized by type
 * @param errorType - The error type to extract
 * @param separator - Separator for string parsing
 * @returns Array of errors for the specified type
 */
export function getErrorsByType(
    errorStateMap: ErrorStateMap,
    errorType: ErrorType,
    separator: string = DEFAULT_ERROR_MESSAGE_SEPARATOR
): string[] {
    const errorString = errorStateMap[errorType];
    return normalizeErrorMessage(errorString, separator);
}

/**
 * Checks if error arrays are equal (order-independent)
 * @param arr1 - First error array
 * @param arr2 - Second error array
 * @returns Whether the arrays contain the same errors
 */
export function areErrorArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
        return false;
    }

    const set1 = new Set(arr1.filter(Boolean));
    const set2 = new Set(arr2.filter(Boolean));

    if (set1.size !== set2.size) {
        return false;
    }

    const items = Array.from(set1);
    for (const item of items) {
        if (!set2.has(item)) {
            return false;
        }
    }

    return true;
}

/**
 * Processes error state changes and determines what updates are needed
 * @param currentState - Current error state map
 * @param newInputErrors - New input errors
 * @param propErrors - Errors from props
 * @param separator - Error message separator
 * @returns Processing result with update instructions
 */
export function processErrorStateChange(
    currentState: ErrorStateMap,
    newInputErrors: string,
    propErrors: string[],
    separator: string = DEFAULT_ERROR_MESSAGE_SEPARATOR
): ErrorProcessingResult {
    // Get current errors by type
    const currentFormErrors = getErrorsByType(currentState, ERROR_TYPES.FORM_ERROR, separator);
    const currentInputErrors = getErrorsByType(currentState, ERROR_TYPES.INPUT_ERROR, separator);
    const currentPropErrors = getErrorsByType(currentState, ERROR_TYPES.PROP_ERROR, separator);

    // Normalize new input errors
    const normalizedInputErrors = normalizeErrorMessage(newInputErrors, separator);

    // Determine changes
    const inputErrorsChanged = !areErrorArraysEqual(currentInputErrors, normalizedInputErrors);
    const propErrorsChanged = !areErrorArraysEqual(currentPropErrors, propErrors);

    if (!inputErrorsChanged && !propErrorsChanged) {
        return {
            finalErrors: mergeErrorArrays([
                currentFormErrors,
                currentInputErrors,
                currentPropErrors,
            ]),
            hasChanges: false,
            shouldUpdateFormError: false,
        };
    }

    // Calculate new final errors
    const newFinalErrors = mergeErrorArrays([currentFormErrors, normalizedInputErrors, propErrors]);

    // Determine if form error needs updating
    const inputErrorsToAddToForm = normalizedInputErrors.filter((error) => {
        return !currentFormErrors.includes(error);
    });

    const propErrorsToAddToForm = propErrors.filter((error) => {
        return !currentFormErrors.includes(error);
    });

    const errorsToAddToForm = [...inputErrorsToAddToForm, ...propErrorsToAddToForm];
    const newFormError = errorsToString([...currentFormErrors, ...errorsToAddToForm], separator);

    return {
        finalErrors: newFinalErrors,
        hasChanges: true,
        newFormError: newFormError || undefined,
        shouldUpdateFormError: errorsToAddToForm.length > 0,
    };
}

/**
 * Removes specific errors from a form error string
 * @param formErrorString - Current form error string
 * @param errorsToRemove - Array of errors to remove
 * @param separator - Error message separator
 * @returns Updated form error string
 */
export function removeErrorsFromFormString(
    formErrorString: string,
    errorsToRemove: string[],
    separator: string = DEFAULT_ERROR_MESSAGE_SEPARATOR
): string | undefined {
    if (!formErrorString || errorsToRemove.length === 0) {
        return formErrorString || undefined;
    }

    const currentErrors = normalizeErrorMessage(formErrorString, separator);
    const filteredErrors = currentErrors.filter((error) => {
        return !errorsToRemove.includes(error);
    });

    return filteredErrors.length > 0 ? errorsToString(filteredErrors, separator) : undefined;
}

/**
 * Validates field name parameter
 * @param fieldName - Field name to validate
 * @throws Error if field name is invalid
 */
export function validateFieldName(fieldName: string): void {
    if (typeof fieldName !== 'string' || fieldName.trim().length === 0) {
        throw new Error('Field name must be a non-empty string');
    }
}

/**
 * Gets a nested property value from an object using dot notation
 * @param obj - Object to get property from
 * @param path - Dot notation path (e.g., 'user.profile.name')
 * @returns Property value or undefined
 */
export function getNestedProperty(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current, key) => {
        return current && typeof current === 'object'
            ? (current as Record<string, unknown>)[key]
            : undefined;
    }, obj);
}

/**
 * Safely converts any value to a string array for error handling
 * @param value - Value to convert
 * @param separator - Separator for string values
 * @returns String array
 */
export function toErrorArray(
    value: unknown,
    separator: string = DEFAULT_ERROR_MESSAGE_SEPARATOR
): string[] {
    if (Array.isArray(value)) {
        return value.filter((item) => {
            return typeof item === 'string' && item.trim().length > 0;
        });
    }

    if (typeof value === 'string') {
        return normalizeErrorMessage(value, separator);
    }

    return [];
}
